import { t } from '@lingui/core/macro';
import { isPresent } from '@luminovo/commons';
import { formatDecimal } from '@luminovo/frontend-utils';
import { FieldValues, Path } from 'react-hook-form';
import { z } from 'zod/v4';

import { FieldController, FieldControllerProps } from '../FieldController';
import { FieldText, FieldTextProps } from './FieldText';

interface ControllerProps<
    TFieldValues extends FieldValues = FieldValues,
    TName extends Path<TFieldValues> = Path<TFieldValues>,
> extends Omit<FieldControllerProps<FieldTextProps, TFieldValues, TName>, 'Field'> {
    required?: boolean;
    /** Inclusive minimum number of characters. */
    min?: number;

    /** Inclusive maximum number of characters. */
    max?: number;
    isEmail?: boolean;

    isUrl?: boolean;
}

export function FieldTextControlled<
    TFieldValues extends FieldValues = FieldValues,
    TName extends Path<TFieldValues> = Path<TFieldValues>,
>({ required, min, max, isEmail, isUrl, validate, ...props }: ControllerProps<TFieldValues, TName>): JSX.Element {
    return (
        <FieldController
            Field={FieldText}
            validate={createValidator({
                required,
                min,
                max,
                isEmail,
                isUrl,
                trim: props.FieldProps?.trim,
                validate,
            })}
            {...props}
        />
    );
}

function createValidator({
    required,
    min,
    max,
    isEmail,
    isUrl,
    trim,
    validate,
}: Pick<ControllerProps, 'required' | 'min' | 'max' | 'isEmail' | 'isUrl' | 'validate'> & {
    trim?: boolean;
}) {
    return (x: unknown): string | undefined => {
        const value = trim && typeof x === 'string' ? x.trim() : x;

        if (required && (!value || (typeof value === 'string' && value.length === 0))) {
            return t`Required`;
        }

        if (typeof value !== 'string' || value.length === 0) {
            return validate ? validate(x) : undefined;
        }

        if (isPresent(min) && value.length < min) {
            return t`Must have at least ${formatDecimal(min)} characters`;
        }

        if (isPresent(max) && value.length > max) {
            return t`Must have at most ${formatDecimal(max)} characters`;
        }

        if (isEmail && !z.email().safeParse(value).success) {
            return t`Must be a valid email`;
        }

        if (isUrl && !z.url().safeParse(value).success) {
            return t`Must be a valid URL`;
        }

        if (validate) {
            return validate(value);
        }
        return undefined;
    };
}
